import { AttributeIdentifiers } from "@lyfegen-platform/graphql-schema/models";
import { Box, makeStyles, Typography } from "@material-ui/core";
import FlexTypographyComponent from "apps/platform-client/client/src/components/FlexTypographyComponent";
import LoadingBox from "apps/platform-client/client/src/components/LoadingBox";
import { EMPTY_VALUE_PLACEHOLDER } from "apps/platform-client/client/src/models/common/data";
import { localeDate } from "apps/platform-client/client/src/utils/locale-date";
import { useTranslation } from "react-i18next";
import { Link } from "react-router-dom";
import { useRecoilValue } from "recoil";
import { blueContrastWithOpacity } from "../../../../hooks/use-map-theme-styles";
import { useDataQuery } from "../../../../hooks/useDataModule";
import { Disease } from "../../../../models/generated/graphql";
import { negotiationRoundValuesState } from "../../../../store/flexible";
import startUpper from "../../../../utils/start-upper";
import { GET_SIMILAR_CASES_BY_CASE_ID } from "../../queries";
import { GetSimilarCasesByCaseId } from "../../types/GetSimilarCasesByCaseId";

export function SimilarCases(props: { caseId: string }) {
	const { t } = useTranslation();
	const styles = useStyles();
	const negotiationRoundValues = useRecoilValue(negotiationRoundValuesState);
	const diseaseIcd10Code =
		Object.values(negotiationRoundValues)
			.map((values: any) => values?.[AttributeIdentifiers.Disease] as Disease)
			.find(Boolean)?.Icd10Code || null;
	const { data, loading } = useDataQuery<GetSimilarCasesByCaseId>(GET_SIMILAR_CASES_BY_CASE_ID, {
		variables: {
			caseId: props.caseId,
			diseaseIcd10Code,
		},
	});

	if (loading) {
		return <LoadingBox fullHeight />;
	}

	return (
		<Box padding={1} width="100%">
			<Typography variant="caption" className={styles.caption}>
				{t("recent-cases")}
			</Typography>
			<Box width="100%" display="flex" className={styles.row}>
				<Box className={styles.dateColumn}>
					<Typography className={styles.tableHeader}>{startUpper(t("date"))}</Typography>
				</Box>
				<Box className={styles.caseIdColumn}>
					<Typography className={styles.tableHeader}>{startUpper(t("case-id"))}</Typography>
				</Box>
				<Box className={styles.refundColumn}>
					<Typography className={styles.tableHeader}>{startUpper(t("refund"))}</Typography>
				</Box>
				<Box className={styles.ratingColumn}>
					<Typography className={styles.tableHeader}>{startUpper(t("rating"))}</Typography>
				</Box>
			</Box>
			{data?.GetSimilarCases.map((caseInfo, index) => (
				<Box key={index} width="100%" display="flex" className={styles.row}>
					<Box className={styles.dateColumn}>
						<Typography variant="body2">{localeDate(caseInfo.creationDate)}</Typography>
					</Box>
					<Box className={styles.caseIdColumn}>
						<Link to={`/cases/case?id=${caseInfo.id}`}>
							<Typography variant="body2">{caseInfo.id}</Typography>
						</Link>
					</Box>
					<Box className={styles.refundColumn}>
						<FlexTypographyComponent
							text={caseInfo.formattedRefundRate !== null ? caseInfo.formattedRefundRate : EMPTY_VALUE_PLACEHOLDER}
							variant="body2"
						/>
					</Box>
					<Box className={styles.ratingColumn}>
						<Typography variant="body2">{caseInfo.rating || EMPTY_VALUE_PLACEHOLDER}</Typography>
					</Box>
				</Box>
			))}
			{data?.GetSimilarCases.length === 0 && (
				<Box display="flex" justifyContent="center" className={styles.row}>
					<Typography variant="h6">{t("no-similar-cases")}</Typography>
				</Box>
			)}
		</Box>
	);
}

const useStyles = makeStyles(() => ({
	dateColumn: {
		width: "30%",
	},
	caseIdColumn: {
		width: "30%",
	},
	refundColumn: {
		width: "20%",
	},
	ratingColumn: {
		width: "20%",
	},
	caption: {
		fontWeight: 300,
	},
	row: {
		padding: "16px",
		"&:nth-child(odd)": {
			backgroundColor: blueContrastWithOpacity,
		},
	},
	tableHeader: {
		fontSize: "14px",
		fontWeight: 600,
		letterSpacing: "0.4px",
	},
}));
